import { useState, useEffect } from 'react';
import { ethers } from 'ethers6';
import { config } from '../utils/config';
import { MAVRK_LENS_ABI } from '../utils/mavrkLensABI';
import { executeWithRpcFailover } from '../utils/rpcProvider';
import { getEthUsdPrice } from '../utils/ethPrice';

// ERC20 ABI for getting token decimals and symbols
const ERC20_ABI = [
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)"
];

export interface NFTTVLData {
  totalNFTTVL: number;
  tokenCount: number;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch NFT positions TVL from MavrkLens contract
 * This queries lockerTVL(true) and adds the IKA token pool value
 */
export function useNFTPositionsTVL(): NFTTVLData {
  const [totalNFTTVL, setTotalNFTTVL] = useState(0);
  const [tokenCount, setTokenCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchNFTPositionsTVL() {
      try {
        setIsLoading(true);
        setError(null);

        console.log('[NFTPositionsTVL] Fetching NFT positions TVL from MavrkLens...');

        let totalTVL = 0;

        let tokensFromLocker = [];
        let amountsFromLocker = [];

        await executeWithRpcFailover(async (provider) => {
          const mavrkLens = new ethers.Contract(
            config.ink.mavrkLensAddress,
            MAVRK_LENS_ABI,
            provider
          );

          // Call lockerTVL(true) to get all locked NFT positions
          const result = await mavrkLens.lockerTVL(true);
          tokensFromLocker = result[0];
          amountsFromLocker = result[1];
          
          console.log('[NFTPositionsTVL] Received', tokensFromLocker.length, 'tokens from lockerTVL');

          const ethPrice = await getEthUsdPrice();
          
          // Process each token and calculate USD value
          for (let i = 0; i < tokensFromLocker.length; i++) {
            const tokenAddress = tokensFromLocker[i];
            const amount = amountsFromLocker[i];
            
            try {
              // Get token decimals
              const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
              const [decimals, symbol] = await Promise.all([
                tokenContract.decimals(),
                tokenContract.symbol().catch(() => '???')
              ]);
              
              // Convert to human readable amount
              const amountHuman = Number(ethers.formatUnits(amount, decimals));
              
              // If it's WETH, multiply by ETH price
              let tokenUSDValue = 0;
              if (tokenAddress.toLowerCase() === '0x4200000000000000000000000000000000000006') {
                tokenUSDValue = amountHuman * ethPrice;
                console.log(`[NFTPositionsTVL] WETH: ${amountHuman.toFixed(4)} ETH = $${tokenUSDValue.toFixed(2)}`);
              } else {
                // For other tokens, we'd need their USD price
                // For now, estimate based on WETH value if we have pair data
                // This is a simplified approach
                tokenUSDValue = 0; // Will need to fetch price from other source
              }
              
              totalTVL += tokenUSDValue;
            } catch (err) {
              console.warn(`[NFTPositionsTVL] Failed to process token ${tokenAddress}:`, err);
            }
          }

          // Add IKA token pool value manually
          // IKA pool: 0x313D214B32676cdC0aC84ac92dDFFbE064C31CFC
          // According to explorer: 4.47205661 WETH = $18,663.23
          // Total TVL (multiply by 2 for both sides of liquidity): $37,326.46
          const ikaValue = 18663.23 * 2;
          totalTVL += ikaValue;
          console.log(`[NFTPositionsTVL] Added IKA pool value: $${ikaValue.toFixed(2)}`);
        });

        if (!isMounted) return;

        // Set token count: tokens.length from lockerTVL + 1 for IKA pool
        setTokenCount(tokensFromLocker.length + 1);
        console.log('[NFTPositionsTVL] Total NFT Positions TVL:', totalTVL.toFixed(2));
        console.log('[NFTPositionsTVL] Token count:', tokensFromLocker.length + 1);

        setTotalNFTTVL(totalTVL);
        setIsLoading(false);
      } catch (err) {
        console.error('[NFTPositionsTVL] Error fetching NFT TVL:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setIsLoading(false);
        }
      }
    }

    fetchNFTPositionsTVL();

    return () => {
      isMounted = false;
    };
  }, []);

  return { totalNFTTVL, tokenCount, isLoading, error };
}

